/**
 * Modal creates popup which on mobile layout fills the entire visible page.
 *
 * Example:
 * <Parent>
 *   <Modal id="UniqueIdForThisModal" isOpen={this.state.modalIsOpen} onClose={handleClose}>
 *     <FormX />
 *   </Modal>
 * </Parent>
 */
import React, { Component, Suspense, lazy, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import { injectIntl } from '../../util/reactIntl';
import useMediaQuery from '../../hooks/useMediaQuery';

import ModalCloseButton from './ModalCloseButton';

import css from './Modal.module.css';

const KEY_CODE_ESCAPE = 27;

// Keep the closing modal mounted this long so the fade-out animation can play
// before it's hidden. Must match the animation durations in Modal.module.css.
// Exported so callers that drive the modal imperatively (and unmount it on
// close) can keep it mounted for the same duration and let the fade-out play.
export const EXIT_ANIMATION_MS = 320;

// Below the desktop breakpoint (--viewportLarge, 1024px) — i.e. mobile & tablet —
// the modal is presented as a bottom sheet instead. See MobileBottomSheet.
const BELOW_DESKTOP_QUERY = '(max-width: 1023px)';

// Lazy + client-only: keeps the (heavy, browser-only) bottom-sheet library out
// of the SSR bundle. Only rendered after mount on small viewports.
const MobileBottomSheet = lazy(() => import('./MobileBottomSheet'));

class Portal extends React.Component {
  constructor(props) {
    super(props);
    this.el = document.createElement('div');
  }

  componentDidMount() {
    // The portal element is inserted in the DOM tree after
    // the Modal's children are mounted, meaning that children
    // will be mounted on a detached DOM node. If a child
    // component requires to be attached to the DOM tree
    // immediately when mounted, for example to measure a
    // DOM node, or uses 'autoFocus' in a descendant, add
    // state to Modal and only render the children when Modal
    // is inserted in the DOM tree.
    this.props.portalRoot.appendChild(this.el);
  }

  componentWillUnmount() {
    this.props.portalRoot.removeChild(this.el);
  }

  render() {
    return ReactDOM.createPortal(this.props.children, this.el);
  }
}

/**
 * Modal
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.scrollLayerClassName overwrite components own css.scrollLayer
 * @param {string?} props.containerClassName overwrite components own css.container
 * @param {string?} props.contentClassName overwrite components own css.content
 * @param {string?} props.isClosedClassName overwrite components own css.isClosed
 * @param {ReactNode} props.closeButtonMessage
 * @param {Object} props.lightCloseButton use lighter close button styles
 * @param {ReactNode?} props.customCloseButton custom close button component (receives onClick prop)
 * @param {string} props.id
 * @param {Object} props.intl
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {Function} props.onManageDisableScrolling
 * @param {boolean} props.usePortal
 * @param {boolean} props.isShowingCloseButton
 * @returns {JSX.Element} Modal element
 */
export class ModalComponent extends Component {
  constructor(props) {
    super(props);
    this.handleBodyKeyUp = this.handleBodyKeyUp.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleScrimClick = this.handleScrimClick.bind(this);
    this.handleResize = this.handleResize.bind(this);

    this.refDiv = React.createRef();
    this.vh = null;
    this.closeTimer = null;

    this.state = {
      portalRoot: null,
      // True while the modal is playing its fade-out. Many callers unmount the
      // modal the instant it closes, so we animate out first and only then tell
      // the parent (see handleClose), which keeps the exit animation working
      // regardless of how the caller toggles the modal.
      isClosing: false,
    };
  }

  componentDidMount() {
    const { id, isOpen, onManageDisableScrolling } = this.props;
    onManageDisableScrolling(id, isOpen);
    window.document.body.addEventListener('keyup', this.handleBodyKeyUp);

    // A hack to update container height for mobile Safari,
    // when resizing happens due to scroll.
    // css.isOpenInPortal has "height: calc(var(--vh, 1vh) * 100)"
    this.vh = window.innerHeight * 0.01;
    window.document.documentElement.style.setProperty('--vh', `${this.vh}px`);
    window.addEventListener('resize', this.handleResize);

    this.setState({
      portalRoot: document.getElementById('portal-root'),
    });
  }

  componentDidUpdate(prevProps) {
    const { id, onManageDisableScrolling } = this.props;
    if (this.props.isOpen !== prevProps.isOpen) {
      onManageDisableScrolling(id, this.props.isOpen);

      // Because we are using portal,
      // we need to set the focus inside Modal manually
      if (this.props.usePortal && this.props.isOpen) {
        this.refDiv.current.focus();
      }

      if (this.props.isOpen) {
        // Opened (or re-opened mid-close): cancel any pending close.
        clearTimeout(this.closeTimer);
        if (this.state.isClosing) {
          this.setState({ isClosing: false });
        }
      } else if (this.state.isClosing) {
        // Our own delayed onClose flipped isOpen; the fade-out already played.
        this.setState({ isClosing: false });
      } else {
        // Closed externally (parent flipped isOpen without going through
        // handleClose): play the fade-out now, then let it hide.
        this.setState({ isClosing: true });
        clearTimeout(this.closeTimer);
        this.closeTimer = setTimeout(() => this.setState({ isClosing: false }), EXIT_ANIMATION_MS);
      }
    }
  }

  componentWillUnmount() {
    const { id, onManageDisableScrolling } = this.props;
    window.document.body.removeEventListener('keyup', this.handleBodyKeyUp);
    window.document.body.removeEventListener('resize', this.handleResize);
    clearTimeout(this.closeTimer);
    onManageDisableScrolling(id, false);
  }

  handleBodyKeyUp(event) {
    const { isOpen } = this.props;
    if (event.keyCode === KEY_CODE_ESCAPE && isOpen) {
      this.handleClose(event);
    }
  }

  handleClose(event) {
    // Both, deliberately: stopPropagation alone leaves a submit button's default
    // action intact, and a modal rendered in place (no usePortal) sits inside
    // whatever form opened it.
    event?.preventDefault();
    event?.stopPropagation();

    // Already animating out — ignore repeat clicks / Esc presses.
    if (this.state.isClosing) {
      return;
    }

    // Play the fade-out first, then notify the parent. Parents commonly unmount
    // the modal on close, so calling onClose immediately would skip the exit
    // animation entirely.
    this.setState({ isClosing: true });
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => {
      const { id, onClose, onManageDisableScrolling } = this.props;
      onManageDisableScrolling(id, false);
      onClose(event);
    }, EXIT_ANIMATION_MS);
  }

  handleScrimClick(event) {
    // Only close when the backdrop itself is clicked, not a bubbled click from
    // the modal content sitting on top of it.
    if (event.target === event.currentTarget) {
      this.handleClose(event);
    }
  }

  handleResize() {
    this.vh = window.innerHeight * 0.01;
    window.document.documentElement.style.setProperty('--vh', `${this.vh}px`);
  }

  render() {
    const {
      children,
      className,
      scrollLayerClassName,
      closeButtonMessage,
      containerClassName,
      contentClassName,
      lightCloseButton,
      customCloseButton,
      intl,
      isClosedClassName = css.isClosed,
      isOpen,
      usePortal,
      isShowingCloseButton = true,
    } = this.props;

    // Use custom close button if provided, otherwise use default
    const closeBtn = isOpen ? (
      customCloseButton ? (
        React.cloneElement(customCloseButton, { onClick: this.handleClose })
      ) : (
        <ModalCloseButton
          onClick={this.handleClose}
          closeButtonMessage={closeButtonMessage}
          lightCloseButton={lightCloseButton}
        />
      )
    ) : null;

    // Modal uses given styles to wrap child components.
    // If props doesn't contain isClosedClassName, styles default to css.isClosed
    // This makes it possible to create ModalInMobile on top of Modal where style modes are:
    // visible, hidden, or none (ModalInMobile's children are always visible on desktop layout.)
    // Stay mounted-as-open while the fade-out animation plays.
    const { isClosing } = this.state;
    const showAsOpen = isOpen || isClosing;
    const isOpenClass = usePortal ? css.isOpenInPortal : css.isOpenInPlace;
    let animationClass = null;
    if (isClosing) {
      animationClass = css.animateOut;
    } else if (isOpen) {
      animationClass = css.animateIn;
    }
    const modalClass = showAsOpen ? classNames(isOpenClass, animationClass) : isClosedClassName;
    const classes = classNames(modalClass, className);
    const scrollLayerClasses = scrollLayerClassName || css.scrollLayer;
    const containerClasses = containerClassName || css.container;
    const portalRoot = this.state.portalRoot;

    // If you want to use Portal https://reactjs.org/docs/portals.html
    // you need to use 'userPortal' flag.
    // ModalInMobile component needs to use the old Modal without the portal
    // because it's relying that the content is rendered inside
    // the DOM hierarchy of the parent component unlike Modal inside Portal.

    return !usePortal ? (
      <div className={classes}>
        <div className={scrollLayerClasses} onClick={this.handleScrimClick}>
          <div className={containerClasses}>
            {isShowingCloseButton && closeBtn}
            <div className={classNames(contentClassName || css.content)}>{children}</div>
          </div>
        </div>
      </div>
    ) : portalRoot ? (
      <Portal portalRoot={portalRoot}>
        <div className={classes}>
          <div className={scrollLayerClasses} onClick={this.handleScrimClick}>
            <div
              className={classNames(containerClasses, css.focusedDiv)}
              ref={this.refDiv}
              tabIndex="-1"
            >
              {closeBtn}
              <div className={classNames(contentClassName || css.content)}>{children}</div>
            </div>
          </div>
        </div>
      </Portal>
    ) : null;
  }
}

const IntlModalComponent = injectIntl(ModalComponent);

/**
 * Responsive Modal: a standard modal on desktop, a bottom sheet on
 * mobile/tablet. The public API is unchanged — every prop is forwarded to
 * whichever presentation is active.
 *
 * The desktop modal is also what renders during SSR and on the first client
 * render (so hydration matches); after mount we switch to the bottom sheet on
 * small viewports.
 *
 * @component
 * @param {Object} props - same props as ModalComponent
 * @returns {JSX.Element}
 */
const Modal = (props) => {
  const isBelowDesktop = useMediaQuery(BELOW_DESKTOP_QUERY);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (mounted && isBelowDesktop) {
    return (
      <Suspense fallback={null}>
        <MobileBottomSheet {...props} />
      </Suspense>
    );
  }

  return <IntlModalComponent {...props} />;
};

export { ModalCloseButton };
export default Modal;
